import React, { useState, useEffect, useCallback, useMemo } from "react";
import { IconType } from "react-icons";
import {
    MdAdd
} from "react-icons/md";
import {
    FaFileExcel,
    FaFileUpload,
    FaFilter,
    FaSearch,
    FaCheckSquare,
    FaRegSquare,
    FaTimes,
    FaCheckCircle,
    FaRegCircle
} from "react-icons/fa";
import { GridColDef, GridRowsProp, GridRenderCellParams } from "@mui/x-data-grid";
import { Button, Dropdown, Form } from "react-bootstrap";
import { InputDataNativa } from "../input-data-nativa";
import Grid from "../grid";
import ModalAlerta from "../modal";
import { AcaoGrid } from "../../services/tipos";
import { ajustarPeriodoPorNovaDataFinal, ajustarPeriodoPorNovaDataInicial } from "../../services/periodo";
import ReactDOMServer from 'react-dom/server';
import { useHistory, useLocation } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { PermissoesCrud, podeAcao } from "../../services/acesso";

// --- INTERFACES ---

export interface FiltroDropdownOpcao {
    label: string;
    value: string | number | boolean;
}

export interface FiltroDropdownConfig {
    key: string;
    label: string;
    placeholder?: string;
    opcoes: FiltroDropdownOpcao[];
    value: string | number | boolean | (string | number | boolean)[];
    onChange: (valor: any) => void;
    multiSelect?: boolean;
}

export interface FiltroDataConfig {
    dataInicio: Date | null;
    dataFim: Date | null;
    onChangeDataInicio: (data: Date | null) => void;
    onChangeDataFim: (data: Date | null) => void;
    labelInicio?: string;
    labelFim?: string;
}

export interface FiltroTextoConfig {
    key: string;
    label: string;
    placeholder?: string;
    value: string;
    onChange: (valor: string) => void;
}

export interface ConfiguracaoFiltros {
    dropdowns?: FiltroDropdownConfig[];
    textos?: FiltroTextoConfig[];
    datas?: FiltroDataConfig;
    aoBuscar?: () => void;
    carregando?: boolean;
    tipoPeriodo?: "realizacao" | "pagamento";
    onChangeTipoPeriodo?: (tipo: "realizacao" | "pagamento") => void;
}

export interface AcaoEmLote {
    titulo: string;
    icone: React.ElementType;
    onClick: (itensSelecionados: (string | number)[]) => void;
    cor?: string;
}

// --- BOTÃO TOGGLE CUSTOMIZADO ---
const FilterDropdownToggle = React.forwardRef<HTMLButtonElement, { children: React.ReactNode; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void }>(
    ({ children, onClick }, ref) => (
        <button
            ref={ref}
            onClick={(e) => { e.preventDefault(); onClick(e); }}
            className="w-full text-sm p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-800 bg-white text-left flex justify-between items-center transition-shadow duration-200"
            style={{ minHeight: "34px" }}
        >
            {children}
        </button>
    )
);

// --- COMPONENTE CONTEÚDO DO DROPDOWN ---
interface DropdownContentProps {
    config: FiltroDropdownConfig;
    onClose: () => void;
}

const DropdownContent: React.FC<DropdownContentProps> = ({ config, onClose }) => {
    const [termoBusca, setTermoBusca] = useState("");
    const [opcoesFiltradas, setOpcoesFiltradas] = useState(config.opcoes);
    const [isFiltering, setIsFiltering] = useState(false);

    useEffect(() => {
        setIsFiltering(true);
        const timeout = setTimeout(() => {
            const lower = termoBusca.toLowerCase();
            setOpcoesFiltradas(
                config.opcoes.filter(op => op.label.toLowerCase().includes(lower))
            );
            setIsFiltering(false);
        }, 300);
        return () => clearTimeout(timeout);
    }, [termoBusca, config.opcoes]);

    // Função auxiliar para limpar valores inválidos (vazios, nulos, undefined)
    const getCleanValues = useCallback(() => {
        const values = Array.isArray(config.value) ? [...config.value] : [];
        return values.filter(v => v !== "" && v !== null && v !== undefined);
    }, [config.value]);

    // Lógica visual para saber se está selecionado
    const isSelected = useCallback((val: string | number | boolean) => {
        if (config.multiSelect) {
            const cleanValues = getCleanValues();
            return cleanValues.includes(val);
        }
        return config.value === val;
    }, [config.value, config.multiSelect, getCleanValues]);

    // ORDENAÇÃO: Traz os selecionados para o topo
    const sortedOptions = useMemo(() => {
        return [...opcoesFiltradas].sort((a, b) => {
            const selectedA = isSelected(a.value);
            const selectedB = isSelected(b.value);

            if (selectedA && !selectedB) return -1;
            if (!selectedA && selectedB) return 1;
            return 0;
        });
    }, [opcoesFiltradas, isSelected]);

    // Verifica se há algo selecionado para mostrar o botão "Limpar"
    const hasSelection = useMemo(() => {
        if (config.multiSelect) {
            const cleanValues = getCleanValues();
            return cleanValues.length > 0;
        }
        return config.value !== "" && config.value !== null && config.value !== undefined;
    }, [config.value, config.multiSelect, getCleanValues]);

    const handleSelect = (val: string | number | boolean) => {
        if (config.multiSelect) {
            // AJUSTE CRÍTICO: Limpa o array atual de valores vazios antes de processar
            const currentValues = getCleanValues();
            const index = currentValues.indexOf(val);

            let newValues;
            if (index >= 0) {
                // Remove
                newValues = currentValues.filter(v => v !== val);
            } else {
                // Adiciona
                newValues = [...currentValues, val];
            }
            // Envia o array limpo
            config.onChange(newValues);
        } else {
            config.onChange(val);
            onClose();
        }
    };

    const handleClear = () => {
        if (config.multiSelect) {
            config.onChange([]);
        } else {
            config.onChange("");
        }
    };

    const toggleSelectAll = () => {
        if (!config.multiSelect) return;

        // AJUSTE: Usa valores limpos
        const currentValues = getCleanValues();
        const allFilteredValues = opcoesFiltradas.map(op => op.value);

        // Verifica se TODOS os itens filtrados estão na seleção atual
        const allSelected = allFilteredValues.every(val => currentValues.includes(val));

        let newValues;
        if (allSelected) {
            // Se todos os visíveis estão marcados, remove eles
            newValues = currentValues.filter(val => !allFilteredValues.includes(val));
        } else {
            // Caso contrário, adiciona os que faltam
            const missing = allFilteredValues.filter(val => !currentValues.includes(val));
            newValues = [...currentValues, ...missing];
        }
        config.onChange(newValues);
    };

    const getSelectAllStatus = () => {
        const cleanValues = getCleanValues();
        
        if (cleanValues.length === 0) return "none";
        // Se não houver opções para filtrar, não há o que selecionar
        if (opcoesFiltradas.length === 0) return "none";

        const allFilteredValues = opcoesFiltradas.map(op => op.value);
        const allSelected = allFilteredValues.every(val => cleanValues.includes(val));

        return allSelected ? "all" : "some";
    };

    return (
        <div className="flex flex-col w-full" onClick={(e) => e.stopPropagation()}>
            {/* Header de Busca - Sticky */}
            <div className="p-2 border-b border-gray-100 bg-white sticky top-0 z-20 rounded-t-md">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-blue-300 focus:shadow-outline-blue sm:text-sm transition duration-150 ease-in-out"
                        placeholder="Pesquisar..."
                        value={termoBusca}
                        onChange={(e) => setTermoBusca(e.target.value)}
                        autoFocus
                    />
                    {termoBusca && (
                        <div
                            className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                            onClick={() => setTermoBusca("")}
                        >
                            <FaTimes className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                        </div>
                    )}
                </div>
            </div>

            {/* Barra de Ações (Limpar / Selecionar Todos) - Sticky abaixo da busca */}
            <div className="bg-white sticky top-[53px] z-10 border-b border-gray-100 shadow-sm">
                {hasSelection && (
                    <div
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-gray-600 bg-white"
                        onClick={handleClear}
                    >
                        <FaTimes className="w-3 h-3" />
                        <span className="text-sm font-medium">Limpar seleção</span>
                    </div>
                )}

                {config.multiSelect && opcoesFiltradas.length > 0 && !isFiltering && (
                    <div
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-[#1F467E]"
                        onClick={toggleSelectAll}
                    >
                        <div className="min-w-[24px]">
                            {getSelectAllStatus() === "all" ? <FaCheckSquare className="w-5 h-5" /> : <FaRegSquare className="text-gray-300 w-5 h-5" />}
                        </div>
                        <span className="text-sm font-medium">
                            {getSelectAllStatus() === "all" ? "Desmarcar Todos" : "Marcar Todos"}
                        </span>
                    </div>
                )}
            </div>

            {/* Lista de Opções - Scrollable */}
            <div className="overflow-y-auto max-h-60 bg-white">
                {isFiltering ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Filtrando...
                    </div>
                ) : sortedOptions.length > 0 ? (
                    sortedOptions.map((opcao) => {
                        const selected = isSelected(opcao.value);
                        return (
                            <div
                                key={String(opcao.value)}
                                className={`px-3 py-2 cursor-pointer flex items-start gap-2 text-sm transition-colors hover:bg-gray-100 ${selected ? 'bg-blue-50' : ''}`}
                                onClick={() => handleSelect(opcao.value)}
                            >
                                <div className="text-[#1F467E] min-w-[24px] mt-0.5 flex-shrink-0">
                                    {config.multiSelect ? (
                                        selected ? <FaCheckSquare className="w-5 h-5" /> : <FaRegSquare className="text-gray-300 w-5 h-5" />
                                    ) : (
                                        selected ? <FaCheckCircle className="w-5 h-5" /> : <FaRegCircle className="text-gray-300 w-5 h-5" />
                                    )}
                                </div>
                                <span className={`whitespace-normal break-words leading-tight ${selected ? 'font-medium text-[#1F467E]' : 'text-gray-700'}`}>
                                    {opcao.label}
                                </span>
                            </div>
                        );
                    })
                ) : (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        Nenhum resultado encontrado
                    </div>
                )}
            </div>
        </div>
    );
};

// --- COMPONENTE LISTAGEM (PRINCIPAL) ---

interface ListagemProps {
    carregando: boolean;
    linhas: GridRowsProp;
    nomeEntidade: string;
    colunas: GridColDef[];
    nomePropriedade: string;
    acoesExtras?: AcaoGrid[];
    exibirAcoesDeCrud?: boolean;
    handleCriar: (Entidade: any) => void;
    handleEditar?: (Entidade: any) => void;
    handleExclusao?: (Entidade: any) => void;
    handleVisualizacao?: (Entidade: any) => void;
    handleSelecionarEntidade: (Entidade: any) => void;
    IconeEntidade: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    iconeAcoes?: IconType;
    rowCount?: number;
    paginationModel?: { page: number; pageSize: number };
    onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
    paginationMode?: 'client' | 'server';
    filtros?: ConfiguracaoFiltros;
    ocultarNovo?: boolean;
    /** Ações restritas por permissão: quem não tem não vê o botão (a API recusaria de qualquer forma). */
    permissoes?: PermissoesCrud;
    exportOptions?: {
        field: string;
        headerName: string;
        valueGetter?: (params: { row: any }) => any;
    }[];
    // Callback opcional: quando definido, a exportação chama esta função para obter o dataset completo
    // (todas as páginas/filtros atuais). Necessário em listagens com paginação server-side para que
    // o arquivo exportado reflita o que o usuário vê na listagem como um todo.
    onExportarBuscarTudo?: () => Promise<any[]>;
    tituloExportacao?: string;
    /** Altura da linha em pixels pelo conteúdo (ver Grid.alturaDaLinha); sem isso vale a padrão. */
    alturaDaLinha?: (row: any) => number;
    onImportar?: () => void;
    headerExtra?: React.ReactNode;
    acoesEmLote?: AcaoEmLote[];
}

const Listagem: React.FC<ListagemProps> = ({
    carregando,
    linhas,
    nomeEntidade,
    colunas,
    nomePropriedade,
    acoesExtras,
    exibirAcoesDeCrud,
    handleCriar,
    handleEditar,
    handleExclusao,
    handleVisualizacao,
    handleSelecionarEntidade,
    IconeEntidade,
    iconeAcoes,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    paginationMode = 'client',
    filtros,
    ocultarNovo = false,
    permissoes,
    exportOptions,
    onExportarBuscarTudo,
    tituloExportacao,
    alturaDaLinha,
    onImportar,
    headerExtra,
    acoesEmLote
}) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [mensagem, setMensagem] = useState<string[]>([]);
    const [itensSelecionados, setItensSelecionados] = useState<(string | number)[]>([]);
    const [showAlerta, setShowAlerta] = useState(false);
    const [entidadeSelecionada, setEntidadeSelecionada] = useState<any>(null);
    const [searchInput, setSearchInput] = useState<string>("");
    const [filteredLinhas, setFilteredLinhas] = useState<GridRowsProp>(linhas);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [abrirModalExportacao, setAbrirModalExportacao] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Controle de qual dropdown está aberto
    const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

    const history = useHistory();
    const location = useLocation();

    const temFiltrosAvancados = useMemo(() => {
        return (filtros?.dropdowns && filtros.dropdowns.length > 0) || (filtros?.textos && filtros.textos.length > 0) || !!filtros?.datas;
    }, [filtros]);

    useEffect(() => {
        if (!temFiltrosAvancados) {
            const searchParams = new URLSearchParams(location.search);
            const searchQuery = searchParams.get('search');
            if (searchQuery) {
                setSearchInput(searchQuery);
            }
        }
    }, [location.search, temFiltrosAvancados]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile && temFiltrosAvancados) setShowFilters(false);
            else if (!mobile && temFiltrosAvancados) setShowFilters(true);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [temFiltrosAvancados]);

    const getFormattedValue = useCallback((coluna: GridColDef, linha: any): string => {
        if (coluna.renderCell) {
            const params: GridRenderCellParams = {
                id: linha.id,
                field: coluna.field,
                value: linha[coluna.field],
                row: linha,
                colDef: { ...coluna, computedWidth: 100, hasBeenResized: false },
                api: {} as any,
                hasFocus: false,
                tabIndex: -1,
                rowNode: {
                    id: linha.id,
                    depth: 0,
                    type: 'leaf',
                    parent: linha.id,
                    groupingKey: linha.id.toString(),
                },
                cellMode: 'view',
                isEditable: false,
                formattedValue: linha[coluna.field],
            };
            const renderizado = coluna.renderCell(params);
            return typeof renderizado === 'string'
                ? renderizado
                : React.isValidElement(renderizado)
                    ? ReactDOMServer.renderToStaticMarkup(renderizado).replace(/<[^>]+>/g, '')
                    : linha[coluna.field]?.toString() || "";
        }
        return linha[coluna.field]?.toString() || "";
    }, []);

    const filterData = useCallback((query: string) => {
        if (temFiltrosAvancados) {
            setFilteredLinhas(linhas);
            return;
        }

        if (!query.trim()) {
            setFilteredLinhas(linhas);
            setIsSearching(false);
            return;
        }

        const lowerQuery = query.trim().toLowerCase();
        const filtered = linhas.filter((linha) =>
            colunas.some((coluna) => {
                const formattedValue = getFormattedValue(coluna, linha).toLowerCase();
                return formattedValue.includes(lowerQuery);
            })
        );
        setFilteredLinhas(filtered);
        setIsSearching(false);
    }, [linhas, colunas, getFormattedValue, temFiltrosAvancados]);

    useEffect(() => {
        setFilteredLinhas(linhas);
    }, [linhas]);

    useEffect(() => {
        if (temFiltrosAvancados) return;

        if (searchTimeout) clearTimeout(searchTimeout);

        if (searchInput.trim() === '') {
            filterData(searchInput);
            return;
        }

        setIsSearching(true);
        const timeout = setTimeout(() => {
            filterData(searchInput);
        }, 500);
        setSearchTimeout(timeout);

        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [searchInput, filterData, temFiltrosAvancados]);

    const handleHide = () => setShowAlerta(false);

    const token = useAppSelector(state => state.token);
    const podeNovo = podeAcao(token, permissoes?.novo);
    const podeEditar = podeAcao(token, permissoes?.editar);
    const podeExcluir = podeAcao(token, permissoes?.excluir);

    const handleExclusaoLocal = (linha: any) => {
        let entidadeIdentificador;

        if (nomePropriedade.includes(";")) {
            const propriedades = nomePropriedade.split(";");
            entidadeIdentificador = propriedades
                .map((prop) =>
                    prop.includes(".")
                        ? prop.split(".").reduce((obj, key) => obj?.[key], linha)
                        : linha[prop]
                )
                .join(" ");
        } else if (nomePropriedade.includes(".")) {
            const propriedades = nomePropriedade.split(".");
            entidadeIdentificador = propriedades.reduce((obj, prop) => obj?.[prop], linha);
        } else {
            entidadeIdentificador = linha[nomePropriedade];
        }

        setEntidadeSelecionada(linha);
        setMensagem([
            "Deseja confirmar a exclusão?",
            "",
            `${nomeEntidade}: ${entidadeIdentificador}`,
            "",
        ]);
        setShowAlerta(true);
    };

    const handleSim = () => {
        handleExclusao?.(entidadeSelecionada);
        setShowAlerta(false);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            const searchParams = new URLSearchParams(location.search);
            const currentSearch = searchParams.get('search') || "";
            
            if (searchInput !== currentSearch) {
                if (searchInput) {
                    searchParams.set('search', searchInput);
                } else {
                    searchParams.delete('search');
                }
                history.replace({ search: searchParams.toString() });
            }
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchInput, history, location.search]);

    const dateToInputValue = (date: Date | null) => {
        return date ? date.toISOString().split("T")[0] : "";
    };

    const inputValueToDate = (value: string) => {
        return value ? new Date(value + "T00:00:00") : null;
    };

    // --- FUNÇÃO DE LABEL CORRIGIDA ---
    const renderDropdownLabel = (config: FiltroDropdownConfig) => {
        // Normaliza o valor atual em um array para contagem segura
        let selectedValues: any[] = [];
        
        if (Array.isArray(config.value)) {
            selectedValues = config.value;
        } else if (config.value !== null && config.value !== undefined && config.value !== "") {
            // Se for single value, colocamos num array
            selectedValues = [config.value];
        }

        // Filtra valores "fantasmas" (ex: null, undefined, string vazia dentro do array)
        selectedValues = selectedValues.filter(v => v !== null && v !== undefined && v !== "");

        // Se não sobrou nada, exibe o placeholder
        if (selectedValues.length === 0) {
            return <span className="text-gray-400">{config.placeholder || "Selecione..."}</span>;
        }

        // Se tem apenas 1 item válido
        if (selectedValues.length === 1) {
            const val = selectedValues[0];
            const opt = config.opcoes.find(o => o.value === val);
            return <span className="text-gray-800 truncate">{opt ? opt.label : val}</span>;
        }

        // Se todos estão selecionados (apenas se houver opções carregadas)
        if (config.multiSelect && selectedValues.length === config.opcoes.length && config.opcoes.length > 0) {
             return <span className="text-gray-800">Todos</span>;
        }

        // Contagem padrão
        return <span className="text-gray-800">{selectedValues.length} selecionados</span>;
    };

    return (
        <div className="flex flex-col h-[calc(100vh-128px)] w-full bg-white-100 overflow-hidden">
            <div className="flex-1 flex flex-col bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex flex-col sm:flex-row items-center justify-between p-3 border-b border-gray-200 gap-2">
                    <div className="flex items-center mb-1 sm:mb-0 self-start sm:self-center gap-3">
                        <div className="flex items-center">
                            <IconeEntidade className="text-blue-800 w-5 h-5 mr-2" />
                            <span className="text-base font-semibold text-gray-800">{nomeEntidade}</span>
                        </div>
                        {headerExtra}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                        <button
                            onClick={() => setAbrirModalExportacao(true)}
                            className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white h-[38px]"
                        >
                            <FaFileExcel className="w-3 h-3" />
                            <span className="font-medium hidden sm:inline">Exportar</span>
                        </button>

                        {onImportar && (
                            <button
                                onClick={onImportar}
                                className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#217346] border border-[#217346] hover:bg-[#217346] hover:text-white h-[38px]"
                            >
                                <FaFileUpload className="w-3 h-3" />
                                <span className="font-medium hidden sm:inline">Importar</span>
                            </button>
                        )}

                        {temFiltrosAvancados && (
                            <button
                                onClick={() => setShowFilters(prev => !prev)}
                                className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors text-[#1F467E] border border-[#1F467E] hover:bg-[#1F467E] hover:text-white"
                                style={{ height: "38px" }}
                            >
                                <div className="flex items-center gap-2">
                                    <FaFilter className="w-3 h-3" />
                                    <span className="font-medium">Filtros</span>
                                    <svg
                                        className={`w-3 h-3 transform transition-transform ${showFilters ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </button>
                        )}

                        {!temFiltrosAvancados && (
                            <div className="relative w-full sm:w-auto">
                                <input
                                    type="text"
                                    placeholder="Digite para Buscar..."
                                    value={searchInput}
                                    onChange={handleSearchChange}
                                    className="border border-gray-400 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 shadow-md placeholder-gray-500 transition duration-300 bg-white w-full sm:max-w-[220px] h-[38px]"
                                />
                                {isSearching && (
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </div>
                                )}
                            </div>
                        )}

                        {!ocultarNovo && podeNovo && (
                            <Button
                                data-tour="btn-cadastrar"
                                style={{
                                    backgroundColor: "#1F467E",
                                    color: "white",
                                    height: "38px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                className="text-sm px-4 rounded border-0 hover:bg-[#17355F]"
                                onClick={handleCriar}
                            >
                                Cadastrar <MdAdd className="ml-2 w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>

                {temFiltrosAvancados && showFilters && (
                    <div className="px-3 py-2 bg-white border-b border-gray-200">
                        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                            {filtros?.textos?.map((textoConfig) => (
                                <div key={textoConfig.key} className="flex-1 min-w-[160px]">
                                    <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
                                        {textoConfig.label}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={textoConfig.placeholder || ""}
                                        value={textoConfig.value}
                                        onChange={(e) => textoConfig.onChange(e.target.value)}
                                        className="w-full h-[34px] text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                                    />
                                </div>
                            ))}

                            {filtros?.dropdowns?.map((dropdownConfig) => (
                                <div key={dropdownConfig.key} className="flex-1 min-w-[160px]">
                                    <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
                                        {dropdownConfig.label}
                                    </Form.Label>
                                    <Dropdown
                                        className="w-full"
                                        show={openDropdownKey === dropdownConfig.key}
                                        onToggle={(isOpen) => setOpenDropdownKey(isOpen ? dropdownConfig.key : null)}
                                    >
                                        <Dropdown.Toggle as={FilterDropdownToggle}>
                                            <div className="flex-1 overflow-hidden">
                                                {renderDropdownLabel(dropdownConfig)}
                                            </div>
                                            <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="w-full p-0 shadow-lg border-0 rounded-md overflow-hidden">
                                            <DropdownContent
                                                config={dropdownConfig}
                                                onClose={() => setOpenDropdownKey(null)}
                                            />
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            ))}

                            {filtros?.datas && (
                                <div className="flex-[1.4] min-w-[340px] max-w-[460px]">
                                    <div className="flex items-center justify-between mb-1">
                                        <Form.Label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-0">Período</Form.Label>
                                        {filtros.onChangeTipoPeriodo && (
                                            <div className="flex rounded overflow-hidden border border-gray-300">
                                                <button type="button" onClick={() => filtros.onChangeTipoPeriodo!("realizacao")}
                                                    className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${filtros.tipoPeriodo === "realizacao" || !filtros.tipoPeriodo ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
                                                    Realização
                                                </button>
                                                <button type="button" onClick={() => filtros.onChangeTipoPeriodo!("pagamento")}
                                                    className={`px-2.5 py-0.5 text-[11px] font-semibold transition-colors border-l border-gray-300 ${filtros.tipoPeriodo === "pagamento" ? "bg-[#1F467E] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
                                                    Pagamento
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <InputDataNativa
                                            value={dateToInputValue(filtros.datas.dataInicio)}
                                            onChange={(valor) => {
                                                const periodo = ajustarPeriodoPorNovaDataInicial(inputValueToDate(valor), filtros.datas?.dataFim ?? null);
                                                filtros.datas?.onChangeDataInicio(periodo.dataInicio);
                                                if (periodo.dataFim !== (filtros.datas?.dataFim ?? null)) {
                                                    filtros.datas?.onChangeDataFim(periodo.dataFim);
                                                }
                                            }}
                                            className="form-control flex-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                                            style={{ height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" }}
                                        />
                                        <span className="text-xs text-gray-500">até</span>
                                        <InputDataNativa
                                            value={dateToInputValue(filtros.datas.dataFim)}
                                            onChange={(valor) => {
                                                const periodo = ajustarPeriodoPorNovaDataFinal(filtros.datas?.dataInicio ?? null, inputValueToDate(valor));
                                                filtros.datas?.onChangeDataFim(periodo.dataFim);
                                                if (periodo.dataInicio !== (filtros.datas?.dataInicio ?? null)) {
                                                    filtros.datas?.onChangeDataInicio(periodo.dataInicio);
                                                }
                                            }}
                                            className="form-control flex-1 text-sm border border-gray-300 rounded bg-white focus:ring-2 focus:ring-blue-800 focus:border-blue-800"
                                            style={{ height: "34px", padding: "0.2rem 0.5rem", lineHeight: "1.4" }}
                                        />
                                    </div>
                                </div>
                            )}

                            {filtros?.aoBuscar && (
                            <div>
                                <Button
                                    style={{ backgroundColor: "#1F467E", color: "white", height: "34px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                                    className="text-sm px-4 rounded border-0 hover:bg-[#17355F]"
                                    onClick={filtros.aoBuscar}
                                    disabled={filtros.carregando || carregando}
                                >
                                    <span className="flex items-center justify-center gap-2 h-full">
                                        {filtros.carregando || carregando ? (
                                            <svg
                                                className="animate-spin h-4 w-4 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                />
                                            </svg>
                                        ) : (
                                            <>
                                                <span>Buscar</span>
                                                <FaSearch className="w-4 h-4" />
                                            </>
                                        )}
                                    </span>
                                </Button>
                            </div>
                        )}
                        </div>
                    </div>
                )}

                {acoesEmLote && itensSelecionados.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-blue-50 border-b border-blue-200">
                        <span className="text-sm font-semibold text-blue-800">
                            {itensSelecionados.length} selecionado(s)
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                            {acoesEmLote.map((acao, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => acao.onClick(itensSelecionados)}
                                    className="text-sm px-3 py-1.5 rounded-md flex items-center gap-2 font-medium transition-colors border"
                                    style={{
                                        color: acao.cor || '#1F467E',
                                        borderColor: acao.cor || '#1F467E',
                                        background: 'white',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = acao.cor || '#1F467E';
                                        e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'white';
                                        e.currentTarget.style.color = acao.cor || '#1F467E';
                                    }}
                                >
                                    <acao.icone size={14} />
                                    {acao.titulo}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setItensSelecionados([])}
                            className="ml-auto text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                            <FaTimes size={12} />
                            Limpar seleção
                        </button>
                    </div>
                )}

                <div className="flex-1 p-2 overflow-auto">
                    <Grid
                        tudoMaiusculo={true}
                        linhas={filteredLinhas}
                        colunas={colunas}
                        carregando={carregando || isSearching || (filtros?.carregando || false)}
                        acoesExtras={acoesExtras}
                        iconeAcoes={iconeAcoes}
                        handleExclusao={handleExclusao && podeExcluir ? handleExclusaoLocal : undefined}
                        handleEditar={podeEditar ? handleEditar : undefined}
                        handleVisualizacao={handleVisualizacao}
                        exibirAcoesDeCrud={exibirAcoesDeCrud}
                        modalExportacaoAberto={abrirModalExportacao}
                        abrirModalExportacao={() => setAbrirModalExportacao(true)}
                        fecharModalExportacao={() => setAbrirModalExportacao(false)}
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={onPaginationModelChange}
                        paginationMode={paginationMode}
                        exportOptions={exportOptions}
                        buscarTudoParaExportar={onExportarBuscarTudo}
                        tituloExportacao={tituloExportacao || nomeEntidade}
                        alturaDaLinha={alturaDaLinha}
                        {...(acoesEmLote && acoesEmLote.length > 0 ? {
                            checkboxSelection: true,
                            itensSelecionados,
                            onSelectionChange: setItensSelecionados,
                        } : {})}
                    />
                </div>

                <ModalAlerta
                    show={showAlerta}
                    ehDeSimNao={true}
                    onSim={handleSim}
                    message={mensagem}
                    onHide={handleHide}
                />
            </div>
        </div>
    );
};

export default Listagem;